import React, {
  useState, useRef, useContext, useEffect, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  MenuList,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
  Button,
} from '@material-ui/core';
import * as Icons from '@material-ui/icons';
import Flag from 'react-world-flags';
import Bugsnag from '@bugsnag/js';
import { getCookie, setCookie } from '../../utils/cookies';
import { useStyles, CustomMenuItem } from './styles';
import {
  COUNTRY_ID,
  COUNTRY_MY,
  COUNTRY_VN,
} from '../../localization/features/constants';
import { AuthContext, SnackbarContext } from '../../contexts';
import { languageOptions as allowedLanguageOptions } from '../../utils/languageOptions';
import { saveLanguagePreference, subscribeLanguageSaveStatus } from '../../utils/languagePreference';

const LANGUAGES = {
  en: { label: 'English', isoCode: 'US', country_code: COUNTRY_MY },
  'id-ID': { label: 'Indonesia', isoCode: 'ID', country_code: COUNTRY_ID },
  'vi-VN': { label: 'Vietnam', isoCode: 'VN', country_code: COUNTRY_VN },
};

const COOKIE_NAME = 'mhub_profile_language_v1';

const LangSelector = () => {
  const authContext = useContext(AuthContext);
  const { actions: { setOpenSnackbar } } = useContext(SnackbarContext);
  const [saving, setSaving] = useState(false);
  const [saveWarning, setSaveWarning] = useState(null);
  const classes = useStyles();
  const { i18n, t } = useTranslation();
  const { country } = authContext.state;
  const anchorRef = useRef(null);

  const getLangByCountry = (countryCode) => {
    if (countryCode === COUNTRY_ID) return 'id-ID';
    if (countryCode === COUNTRY_VN) return 'vi-VN';
    return 'en';
  };

  const getInitialLang = () => {
    if (Object.keys(LANGUAGES).includes(i18n.language)) return i18n.language;
    const cookieLang = getCookie(COOKIE_NAME);
    if (cookieLang && LANGUAGES[cookieLang]) {
      return cookieLang;
    }
    return getLangByCountry(country.country_code);
  };

  const [currentLang, setCurrentLang] = useState(getInitialLang());
  const [open, setOpen] = useState(false);

  // Initialization belongs to the authenticated provider, not this responsive menu.
  useEffect(() => {
    const update = (language) => setCurrentLang(language);
    i18n.on('languageChanged', update);
    update(i18n.language);
    return () => i18n.off('languageChanged', update);
  }, [i18n]);

  useEffect(() => subscribeLanguageSaveStatus((pending) => setSaveWarning(pending ? t('language.save_unconfirmed', 'Language save is still unconfirmed. This tab keeps your selection. Wait for the connection to recover before retrying.') : null)), [t]);

  const handleToggle = () => setOpen((prev) => !prev);

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  const handleChange = async (langCode) => {
    i18n.changeLanguage(langCode);
    setCurrentLang(langCode);
    setOpen(false);
    setSaving(true);
    try {
      await saveLanguagePreference(langCode);
      setCookie(COOKIE_NAME, langCode, 365);
    } catch (error) {
      if (!error.blocked) Bugsnag.notify(error);
      const message = error.code === 'LANGUAGE_SAVE_UNCONFIRMED'
        ? t('language.save_unconfirmed', 'Language save is still unconfirmed. This tab keeps your selection. Wait for the connection to recover before retrying.')
        : t('language.save_failed', 'Could not save your language preference.');
      if (error.code !== 'LANGUAGE_SAVE_UNCONFIRMED') setSaveWarning(null);
      if (error.code !== 'LANGUAGE_SAVE_UNCONFIRMED') setOpenSnackbar({ variant: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const languageOptions = useMemo(
    () => allowedLanguageOptions(Object.entries(LANGUAGES).map(([code, value]) => ({
      ...value, code, value: code.split('-')[0],
    })), authContext.state.user?.company?.activeLanguageCodes, currentLang.split('-')[0]),
    [authContext.state.user?.company?.activeLanguageCodes, currentLang],
  );

  return (
    <>
      <Button
        ref={anchorRef}
        disabled={saving}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        className={classes.flagOnly}
      >
        <Flag code={LANGUAGES[currentLang]?.isoCode} className={classes.flagIcon} />
        <Icons.ArrowDropDown className={classes.arrow} />
      </Button>

      <Popper open={!!saveWarning && !open} anchorEl={anchorRef.current} placement="bottom-end" style={{ zIndex: 1201 }}>
        <Paper role="alert" style={{ padding: 12, maxWidth: 320 }}>{saveWarning}</Paper>
      </Popper>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        className={classes.popper}
        disablePortal
      >
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList id="language-menu" disablePadding>
              {languageOptions.map(({
                code, label, isoCode, disabled,
              }) => (
                <CustomMenuItem
                  key={code}
                  disabled={disabled}
                  selected={currentLang === code}
                  onClick={() => handleChange(code)}
                >
                  <Flag code={isoCode} className={classes.flagIcon} />
                  <Typography>{label}</Typography>
                </CustomMenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </>
  );
};

export default LangSelector;
